import { axiosInstance } from "../../Authentication/AxiosInterceptor";
import { isSetDate, toDate } from "../../Helpers/Dates";
import { AreaDisplayInfo } from "../../Types/AreaDisplayInfo/AreaDisplayInfo";
import { IProductQuestionnaire } from "../../Types/Products/IProductQuestionnaire";
import { AreaExportData } from "./types";

export type FetchAreaExportDataOptions = {
  /** Map of area id → display row from the list (for summary columns). */
  displayInfoById?: Record<string, AreaDisplayInfo | undefined>;
};

type AreaQuestionnaireClientResult = {
  areaId: string;
  questionnaire: IProductQuestionnaire;
  dueDate?: string;
  ownerName?: string;
  completedDate?: string;
};

function parseApiDate(value: string | undefined): Date | undefined {
  if (!value?.trim()) return undefined;
  if (!isSetDate(value)) return undefined;
  return toDate(value);
}

/** Auth0-style subs are not display names — prefer a resolved name when merging sources. */
function looksLikeUserSub(value: string | undefined): boolean {
  return !!value && value.includes("|");
}

function pickResolvedOwnerName(
  fromList: string | undefined,
  fromApi: string | undefined
): string | undefined {
  const candidates = [fromList, fromApi].filter(Boolean) as string[];
  if (candidates.length === 0) return undefined;
  return candidates.find((name) => !looksLikeUserSub(name)) ?? candidates[0];
}

/** Prefer list metadata; fill gaps from bulk questionnaire API export summary. */
function mergeExportDisplayInfo(
  areaId: string,
  fromList: AreaDisplayInfo | undefined,
  fromApi: Pick<
    AreaQuestionnaireClientResult,
    "dueDate" | "ownerName" | "completedDate"
  >
): AreaDisplayInfo | undefined {
  const completedDate =
    fromList?.completedDate ?? parseApiDate(fromApi.completedDate);
  const dueDate = fromList?.dueDate ?? parseApiDate(fromApi.dueDate);
  const ownerName = pickResolvedOwnerName(fromList?.ownerName, fromApi.ownerName);

  if (!fromList && !completedDate && !dueDate && !ownerName) {
    return undefined;
  }

  if (fromList) {
    return {
      ...fromList,
      dueDate: fromList.dueDate ?? dueDate ?? fromList.dueDate,
      ownerName: pickResolvedOwnerName(fromList.ownerName, ownerName),
      completedDate: fromList.completedDate ?? completedDate,
    };
  }

  return {
    id: areaId,
    title: "",
    dueDate: dueDate ?? new Date(),
    status: "Incomplete",
    participantCount: 0,
    groups: [],
    roles: [],
    organisations: [],
    departments: [],
    percentage: 0,
    legacy: false,
    archived: false,
    canArchive: false,
    canExport: false,
    canView: false,
    ownerName,
    completedDate,
  };
}

type LinkedEvidenceDto = {
  id: string;
  fileName: string;
};

type LinkedEvidenceByAreaResponse = {
  areaId: string;
  byCriteriaId: Record<string, LinkedEvidenceDto[] | undefined>;
};

function mapEvidenceToFileNames(
  byCriteriaId: Record<string, LinkedEvidenceDto[] | undefined> | undefined
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!byCriteriaId) return out;
  for (const [criteriaId, files] of Object.entries(byCriteriaId)) {
    if (!files?.length) continue;
    out[criteriaId] = files.map((f) => f.fileName).filter(Boolean);
  }
  return out;
}

/**
 * Loads questionnaire payloads via bulk API, then linked evidence per area in parallel.
 */
export async function fetchAreaExportData(
  areaIds: string[],
  options?: FetchAreaExportDataOptions
): Promise<AreaExportData[]> {
  const unique = Array.from(new Set(areaIds.filter(Boolean)));
  if (unique.length === 0) {
    return [];
  }

  const qResponse = await axiosInstance.post<AreaQuestionnaireClientResult[]>(
    "GetQuestionnairesClientByAreaIds",
    { areaIds: unique }
  );

  const items = qResponse.data ?? [];
  const byAreaId = new Map(items.map((item) => [item.areaId, item]));
  const orderedItems: AreaQuestionnaireClientResult[] = [];
  for (const id of unique) {
    const item = byAreaId.get(id);
    if (item) orderedItems.push(item);
  }
  for (const item of items) {
    if (!unique.includes(item.areaId)) {
      orderedItems.push(item);
    }
  }

  const evidenceResults = await Promise.all(
    orderedItems.map(async (item) => {
      try {
        const r = await axiosInstance.get<LinkedEvidenceByAreaResponse>(
          `GetEvaluateLinkedEvidenceByAreaId/${item.areaId}`
        );
        return {
          areaId: item.areaId,
          evidenceByCriteriaId: mapEvidenceToFileNames(r.data?.byCriteriaId),
        };
      } catch {
        return {
          areaId: item.areaId,
          evidenceByCriteriaId: {} as Record<string, string[]>,
        };
      }
    })
  );

  const evidenceByAreaId = new Map(
    evidenceResults.map((e) => [e.areaId, e.evidenceByCriteriaId])
  );

  return orderedItems.map((item) => ({
    areaId: item.areaId,
    questionnaire: item.questionnaire,
    displayInfo: mergeExportDisplayInfo(
      item.areaId,
      options?.displayInfoById?.[item.areaId],
      item
    ),
    evidenceByCriteriaId: evidenceByAreaId.get(item.areaId) ?? {},
  }));
}
